/**
 * 통합 네비게이션 컴포넌트 (반응형)
 * 상단 헤더(로고, 유틸리티 메뉴)와 메인 메뉴를 포함합니다.
 * 데스크톱: 드롭다운 메뉴
 * 모바일: 햄버거 메뉴 + 사이드바
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { MAIN_MENU } from '../constant/menuData';
import logoImage from '../assets/seem_logo_w.png';
import bgLineImage from '../assets/bg_line.png';
import btnImg from '../assets/menu-icon.png';
import moreIcon from '../assets/more.png';
import { useIsMobile } from '../hooks/useMediaQuery';

/**
 * Nav 컴포넌트
 * NavHeader와 NavContainer를 포함하는 통합 네비게이션 영역
 */
function Nav() {
  // 모바일 여부 확인
  const isMobile = useIsMobile();

  // 데스크톱: 메뉴 hover 상태 관리
  const [hoverIndex, setHoverIndex] = useState(null);
  const open = hoverIndex !== null;

  // 모바일: 사이드바 열림/닫힘 상태
  const [isOpen, setOpen] = useState(false);
  // 모바일: 각 메뉴의 펼침/접힘 상태 (MAIN_MENU 길이만큼)
  const [expandedMenus, setExpandedMenus] = useState(
    Array(MAIN_MENU.length).fill(false)
  );
  const location = useLocation();
  const sidebarRef = useRef();

  /**
   * 마우스 오버 핸들러 (데스크톱)
   * 메인 메뉴에 마우스를 올리면 서브메뉴가 펼쳐집니다.
   */
  const onMouseOver = (e) => {
    if (!isMobile) {
      const li = e.target.closest('li[data-index]');
      if (li) {
        const idx = Number(li.getAttribute('data-index'));
        if (!Number.isNaN(idx)) setHoverIndex(idx);
      }
    }
  };

  /**
   * 링크 클릭 핸들러
   * 메뉴를 클릭하면 서브메뉴를 닫습니다.
   */
  const handleLinkClick = () => setHoverIndex(null);

  /**
   * 햄버거 메뉴 토글 (모바일)
   */
  const toggleMenu = () => {
    setOpen((prevState) => {
      const newState = !prevState;
      // 네비게이션이 닫힐 때 expandedMenus 초기화
      if (!newState) {
        setExpandedMenus(Array(MAIN_MENU.length).fill(false));
      }
      return newState;
    });
  };

  /**
   * 모바일 메뉴 아코디언 토글 (모바일)
   * @param {number} index - 토글할 메뉴 인덱스
   */
  const toggleMobileMenu = (index) => {
    setExpandedMenus((prev) => {
      const newExpanded = [...prev];
      newExpanded[index] = !newExpanded[index];
      return newExpanded;
    });
  };

  /**
   * 현재 경로에 맞는 메뉴 인덱스 찾기
   * @returns {number|null} - 해당하는 메뉴 인덱스, 없으면 null
   */
  const findActiveMenuIndex = useCallback(() => {
    const path = location.pathname;

    // 각 메인 메뉴의 경로나 서브메뉴 경로와 매칭
    for (let i = 0; i < MAIN_MENU.length; i++) {
      const menu = MAIN_MENU[i];

      // 메인 메뉴 경로 체크
      if (path.startsWith(menu.path)) {
        return i;
      }

      // 서브메뉴 경로 체크
      const hasMatchingSubMenu = menu.subMenus.some((subMenu) =>
        path.startsWith(subMenu.path)
      );

      if (hasMatchingSubMenu) {
        return i;
      }
    }

    return null;
  }, [location.pathname]);

  /**
   * 페이지 이동 시 처리 (모바일)
   * - 사이드바 닫기
   * - 현재 페이지에 맞는 메뉴만 열기
   */
  useEffect(() => {
    if (isMobile) {
      setOpen(false);

      // 현재 페이지에 해당하는 메뉴만 열기
      const activeIndex = findActiveMenuIndex();
      const newExpandedMenus = Array(MAIN_MENU.length).fill(false);
      if (activeIndex !== null) {
        newExpandedMenus[activeIndex] = true;
      }
      setExpandedMenus(newExpandedMenus);
    }
  }, [location, isMobile, findActiveMenuIndex]);

  /**
   * 사이드바 외부 클릭 시 닫기 (모바일)
   */
  const handleClose = useCallback(
    (event) => {
      if (
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        if (event.target.id !== 'toggle') {
          toggleMenu();
        }
      }
    },
    [isOpen]
  );

  useEffect(() => {
    if (isMobile && isOpen) {
      document.addEventListener('click', handleClose);
      return () => {
        document.removeEventListener('click', handleClose);
      };
    }
  }, [isOpen, isMobile, handleClose]);

  return (
    <StyledNav $isMobile={isMobile}>
      {/* ========== 모바일 버전 ========== */}
      {isMobile && (
        <>
          <MobileNav>
            {/* 로고 영역 */}
            <MobileLogoHeader>
              <Link
                to="/"
                title="홈 바로가기"
                style={{
                  width: '52%',
                  height: '55px',
                  display: 'block',
                  marginLeft: '25%',
                  float: 'left',
                }}
              >
                <HiddenText>중구행복돌봄센터</HiddenText>
              </Link>

              {/* 햄버거 메뉴 버튼 */}
              <MenuButton id="toggle" onClick={toggleMenu}>
                <img src={btnImg} alt="메뉴" id="toggle" />
              </MenuButton>
            </MobileLogoHeader>
          </MobileNav>

          {/* 사이드바 - MAIN_MENU 데이터 사용 */}
          {isOpen && (
            <SidebarContainer ref={sidebarRef}>
              <MobileSidebarMenu>
                {MAIN_MENU.map((menu, index) => (
                  <MobileSidebarItem key={menu.id}>
                    <MobileMenuButton
                      $isLast={index === MAIN_MENU.length - 1}
                      onClick={() => toggleMobileMenu(index)}
                    >
                      {menu.label}
                    </MobileMenuButton>

                    {/* 서브메뉴 - 펼쳐진 경우에만 표시 */}
                    {expandedMenus[index] && (
                      <MobileSubMenu>
                        {menu.subMenus.map((subMenu) => (
                          <li key={subMenu.id}>
                            <Link to={subMenu.path}>{subMenu.label}</Link>
                          </li>
                        ))}
                      </MobileSubMenu>
                    )}
                  </MobileSidebarItem>
                ))}

                {/* 추가 링크 배너 */}
                <MobileBannerItem>
                  <Link to="/sitemap" title="사이트맵">
                    사이트맵
                  </Link>
                  <a
                    href="https://www.seempower.kr/"
                    target="_blank"
                    rel="noreferrer"
                    title="에스이임파워협동조합 홈페이지로 이동"
                  >
                    협동조합 바로가기
                  </a>
                </MobileBannerItem>
              </MobileSidebarMenu>
            </SidebarContainer>
          )}
        </>
      )}

      {/* ========== 데스크톱 버전 ========== */}
      {!isMobile && (
        <>
          {/* NavHeader 영역 */}
          <NavHeader>
            {/* 사이트 로고 - 홈으로 이동 */}
            <Logo>
              <LogoLink to="/" title="홈 바로가기">
                <HiddenText>중구행복돌봄센터</HiddenText>
              </LogoLink>
            </Logo>

            {/* 유틸리티 메뉴 영역 */}
            <GlobalMenu>
              <GlobalMenuTitle>
                <strong>유틸메뉴</strong>
              </GlobalMenuTitle>

              {/* 홈으로 링크 */}
              <GlobalMenuItem>
                <Link to="/">홈으로</Link>
              </GlobalMenuItem>

              {/* 사이트맵 링크 */}
              <GlobalMenuItem>
                <Link to="/sitemap" title="사이트맵">
                  사이트맵
                </Link>
              </GlobalMenuItem>

              {/* 외부 링크 - 협동조합 홈페이지 */}
              <GlobalMenuItem>
                <a
                  href="https://www.seempower.kr/"
                  target="_blank"
                  rel="noreferrer"
                  title="에스이임파워협동조합 홈페이지로 이동"
                >
                  협동조합 바로가기
                </a>
              </GlobalMenuItem>
            </GlobalMenu>
          </NavHeader>

          {/* NavContainer 영역 (메인 메뉴) */}
          <MenuContainer $open={open} onMouseLeave={() => setHoverIndex(null)}>
            <HiddenText as="h2">메인메뉴</HiddenText>

            <Navigation onMouseOver={onMouseOver}>
              <Depth1>
                {MAIN_MENU.map((menu, index) => (
                  <Depth1Item
                    key={menu.id}
                    $open={open}
                    data-index={index}
                    $first={index === 0}
                    $last={index === MAIN_MENU.length - 1}
                    $active={hoverIndex === index}
                  >
                    <Link onClick={handleLinkClick} to={menu.path}>
                      {menu.label}
                    </Link>
                    <Depth2>
                      {menu.subMenus.map((subMenu) => (
                        <li key={subMenu.id}>
                          <Link onClick={handleLinkClick} to={subMenu.path}>
                            {subMenu.label}
                          </Link>
                        </li>
                      ))}
                    </Depth2>
                  </Depth1Item>
                ))}
              </Depth1>
            </Navigation>
          </MenuContainer>
        </>
      )}
    </StyledNav>
  );
}

export default Nav;

/* ========== Styled Components ========== */

/* Nav 전체 컨테이너 */
const StyledNav = styled.div`
  /* 데스크톱 기본 스타일 */
  position: relative;
  min-width: ${(props) => (props.$isMobile ? '100%' : '1020px')};
  width: 100%;
  height: ${(props) => (props.$isMobile ? 'auto' : '162px')};
  z-index: 1001; /* 모든 페이지 콘텐츠보다 위에 표시 */

  &:after {
    display: block;
    clear: both;
    content: '';
  }

  /* 모바일 스타일 */
  @media (max-width: 1023px) {
    min-width: 100%;
  }
`;

/* ========== NavHeader 스타일 ========== */

const NavHeader = styled.div`
  position: relative;
  width: 100%;
  height: 110px;
  margin: 0 auto;
  background: url(${logoImage}) 100% no-repeat;
  background-size: 280px 65px;
  background-position: center;
`;

const HiddenText = styled.span`
  position: absolute;
  overflow: hidden;
  top: 0;
  visibility: hidden;
  width: 1px;
  height: 1px;
`;

const Logo = styled.h1`
  position: absolute;
  top: 37px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  width: 350px;
  height: 55px;
  margin: 0;

  @media (max-width: 1200px) {
    width: 300px;
  }
`;

const LogoLink = styled(Link)`
  width: 100%;
  height: 55px;
  display: block;
`;

const GlobalMenu = styled.dl`
  position: absolute;
  right: 0;
  top: 12px;
  z-index: 1;
  text-align: left;
  margin: 0;
  padding: 0;
`;

const GlobalMenuTitle = styled.dt`
  visibility: hidden;
  position: absolute;
  font-size: 0;
  width: 0;
  height: 0;
  line-height: 0;
  margin: 0;
  padding: 0;
  background: none;
`;

const GlobalMenuItem = styled.dd`
  float: left;
  display: block;
  padding-left: 12px;
  padding-right: 12px;
  height: 16px;
  background: url(${bgLineImage}) right 50% no-repeat;
  margin: 0;

  a {
    display: block;
    font-family: 'koPubWorld DotumM';
    font-size: 12px;
    text-decoration: none;

    &:link,
    &:visited,
    &:active {
      color: #323232;
    }

    &:hover,
    &:focus {
      color: #000;
      text-decoration: underline;
    }
  }
`;

/* ========== NavContainer (메인 메뉴) 스타일 ========== */

const MenuContainer = styled.div`
  z-index: 1000;
  height: ${({ $open }) => ($open ? '416px' : '52px')};
  width: 100%;
  overflow: hidden;
  background: linear-gradient(
    #e5857c 52px,
    rgba(245, 210, 206, 1) 50px,
    rgba(245, 210, 206, 1)
  );

  a {
    color: #fff;
    text-decoration: none;
  }
`;

const Navigation = styled.nav`
  position: relative;
  width: 100%;
  max-width: 1020px;
  margin: 0 auto;
  text-align: center;
`;

const Depth1 = styled.ol`
  position: absolute;
  width: 100%;
  z-index: 1000;
  font-size: 20px;
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;

  & > li {
    flex: 1;
    box-sizing: border-box;
    border-right: 1px solid #ffffff;
  }

  & > li:first-child {
    border-left: 1px solid #ffffff;
  }
`;

const Depth1Item = styled.li`
  margin: 0;

  /* 기본 링크 스타일 */
  > a {
    display: block;
    width: 100%;
    height: 52px;
    line-height: 52px;
    text-align: center;
    font-size: 22px;
    font-weight: bold;
    font-family: 'Nanum Gothic Bold';
    color: #fff;
    text-decoration: none;
  }

  /* 메뉴가 열려 있을 때만 hover 색상 변경 */
  > a:hover {
    color: ${({ $open }) => ($open ? '#e5857c' : '#fff')};
    text-decoration: none;
  }

  /* 활성화된 메뉴 스타일 */
  ${({ $active }) =>
    $active &&
    css`
      background-color: rgba(255, 255, 255, 1);
      > a {
        color: #e5857c;
      }
    `}
`;

const Depth2 = styled.ol`
  display: block;
  height: 365px;
  margin: 0;
  padding: 0;
  list-style: none;
  background-color: #f5d2ce;
  color: #666666;
  font-size: 17px;
  font-family: 'Nanum Square Bold';

  > li {
    float: none;
    margin: 0;
    border: 0;
    background: none;
  }

  a {
    display: block;
    height: 52px;
    line-height: 52px;
    text-align: center;
    font-size: 18px;
    color: #666666;
    text-decoration: none;
  }

  a:hover {
    color: #e5857c;
    text-decoration: none;
    background-color: rgba(255, 255, 255, 0.5);
  }
`;

/* ========== 모바일 전용 스타일 컴포넌트 ========== */

const MobileNav = styled.div`
  position: relative;
  float: left;
  width: 100%;
  height: 8%;
  background: url(${logoImage}) center center no-repeat;
  background-size: 50% 50%;

  @media (max-width: 320px) {
    height: 7%;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    height: 85px;
  }
`;

const MobileLogoHeader = styled.h1`
  position: relative;
  width: 100%;
  height: 100%;
`;

const MenuButton = styled.button`
  width: 10%;
  height: 50px;
  float: right;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  img {
    height: 25px;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    width: 10%;
    height: 100%;

    img {
      height: 35px;
    }
  }
`;

const SidebarContainer = styled.div`
  position: fixed;
  width: 65%;
  height: 100vh;
  background-color: #e5857c;
  z-index: 1000;
  top: 0;
  left: 0;
  overflow-y: auto;

  /* 스크롤바 숨기기 */
  -ms-overflow-style: none; /* IE, Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

/* 모바일 사이드바 메뉴 스타일 */

const MobileSidebarMenu = styled.ol`
  position: absolute;
  margin-top: 55px;
  font-size: 30px;
  width: 100%;
  margin-left: 0;
  padding: 0;
  list-style: none;

  @media (min-width: 768px) and (max-width: 1023px) {
    margin-top: 70px;
  }
`;

const MobileSidebarItem = styled.li`
  width: 100%;
  margin-left: 0;
`;

const MobileMenuButton = styled.button`
  font-size: 20px;
  font-family: 'Nanum Gothic Bold';
  display: block;
  color: #ffffff;
  border: none;
  border-top: 1px solid #cecece;
  width: 100%;
  height: 55px;
  line-height: 55px;
  box-sizing: border-box;
  text-decoration: none;
  padding-left: 20px;
  background: url(${moreIcon}) 90% center no-repeat;
  background-color: transparent;
  background-size: 20px;
  text-align: left;
  cursor: pointer;

  ${({ $isLast }) =>
    $isLast &&
    css`
      border-bottom: 1px solid #cecece;
    `}

  @media (min-width: 768px) and (max-width: 1023px) {
    font-size: 25px;
    height: 70px;
    line-height: 70px;
  }
`;

const MobileSubMenu = styled.ol`
  font-size: 17px;
  display: block;
  background-color: #f5d2ce;
  color: #666666;
  font-family: 'Nanum Square Bold';
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    float: none;
    background-color: none;
    border: 0;
  }

  li a {
    font-size: 18px;
    padding-left: 0px;
    height: 52px;
    line-height: 52px;
    box-sizing: border-box;
    text-decoration: none;
    text-align: center;
    color: #666666;
    background: none;
    display: block;
    border-top: 1px solid #cecece;
    width: 100%;

    &:hover {
      color: #e5857c;
      text-decoration: none;
      background-color: rgba(229, 133, 124, 0.2);
    }
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    li a {
      font-size: 20px;
      height: 55px;
      line-height: 55px;
    }
  }
`;

const MobileBannerItem = styled.li`
  margin-top: 100px;
  text-align: right;

  a {
    border: 0;
    background: none;
    font-family: 'koPubWorld DotumM';
    font-size: 12px;
    height: 40px;
    padding-left: 0px;
    padding-right: 20px;
    margin-left: 0px;
    margin-right: 20px;
    line-height: 40px;
    color: #ffffff;
    display: inline-block;
    text-decoration: none;
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    a {
      font-size: 15px;
      height: 55px;
      line-height: 55px;
    }
  }
`;
